import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { listLookup } from '../../api/masterData';

interface BulkWhatsAppDialogProps {
  open: boolean;
  leadCount: number;
  errorMessage: string | null;
  resultSummary: { total: number; sent: number; failed: number } | null;
  onClose: () => void;
  onSubmit: (input: { templateId?: number; body?: string }) => Promise<void>;
}

export const BulkWhatsAppDialog = ({
  open,
  leadCount,
  errorMessage,
  resultSummary,
  onClose,
  onSubmit,
}: BulkWhatsAppDialogProps) => {
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ['lookup', '/master-data/whatsapp-templates'],
    queryFn: () => listLookup('/master-data/whatsapp-templates', { activeOnly: true }),
    enabled: open,
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ templateId: templateId || undefined, body: templateId ? undefined : body });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Send WhatsApp to {leadCount} lead(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {resultSummary && (
            <Alert severity={resultSummary.failed === 0 ? 'success' : 'warning'}>
              Sent {resultSummary.sent}/{resultSummary.total} ({resultSummary.failed} failed)
            </Alert>
          )}
          <TextField
            select
            label="Template"
            fullWidth
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Free text</em>
            </MenuItem>
            {templatesQuery.data?.map((t) => (
              <MenuItem key={t.id as number} value={t.id as number}>
                {String(t.name)}
              </MenuItem>
            ))}
          </TextField>
          {!templateId && (
            <TextField
              label="Message"
              multiline
              minRows={2}
              fullWidth
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Supports {'{{firstName}}'}, {'{{lastName}}'} and {'{{leadNumber}}'} placeholders.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={(!templateId && !body.trim()) || submitting} onClick={handleSubmit}>
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
